
import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Input,
  Select,
  Button,
  Table,
  Space,
  Tag,
  Typography,
  Pagination,
  Dropdown,
  Menu,
  Drawer,
  message,
} from "antd";

import {
  SearchOutlined,
  MenuOutlined,
  CopyOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { TLSApi } from "./Tls_ApiService";


const { Text, Title } = Typography;
const { Option } = Select;


/* ============================================================
   MAIN COMPONENT
============================================================ */

export default function StaffManagementUI() {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  // Add / Edit form
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  /* ============================================================
     LOAD STAFF LIST
  ============================================================ */
  // const loadStaff = async () => {
  //   setLoading(true);
  //   try {
  //     const res = await TLSApi.getAllStaffUsers(page);
  //     if (res.data.status === true) {
  //       setData(res.data.data);
  //       setTotalRecords(res.data.total_records);
  //     }
  //   } catch (err) {
  //     console.log("Staff load error →", err);
  //   }
  //   setLoading(false);
  // };
const loadStaff = async () => {
  setLoading(true);

  try {
    const res = await TLSApi.getAllStaffUsers(page, 10);

    if (res.data.status === true) {
      setData(res.data.data);       // API returns only 10 rows
      setTotalRecords(res.data.total_records);
    }
  } catch (err) {
    console.log("Error loading staff", err);
  }

  setLoading(false);
};

  useEffect(() => {
    loadStaff();
  }, [page]);

  /* ============================================================
     ADD NEW STAFF USER
  ============================================================ */
  const handleAddStaff = async () => {
    const values = addForm.getFieldsValue();

    try {
      await TLSApi.addStaffUser(values);
      message.success("Staff user added");
      addForm.resetFields();
      loadStaff();
    } catch (err) {
      message.error("Add failed");
    }
  };

  /* ============================================================
     EDIT USER
  ============================================================ */
  const openEdit = (row) => {
    setEditRecord(row);
    setEditOpen(true);
    editForm.setFieldsValue(row);
  };

  const handleUpdateStaff = async () => {
    const values = editForm.getFieldsValue();

    const payload = {
      su_id: editRecord.su_id,
      full_name: values.full_name,
      username: values.username,
      password: values.password ?? "",
      role: values.role,
      sub_roles: values.sub_roles,
      dept: values.dept,
      status: values.status,
    };

    try {
      await TLSApi.updateStaffUser(payload);
      message.success("Updated successfully");
      setEditOpen(false);
      loadStaff();
    } catch (err) {
      message.error("Update failed");
    }
  };

  /* ============================================================
     TABLE COLUMNS
  ============================================================ */
  const actionMenu = (row) => (
    <Menu>
      <Menu.Item onClick={() => openEdit(row)}>⚙ Edit</Menu.Item>
      <Menu.Item onClick={() => console.log("Print QR", row)}>🧾 Print QR</Menu.Item>
      <Menu.Item onClick={() => console.log("Print Barcode", row)}>🧾 Print Barcode</Menu.Item>
    </Menu>
  );

  const columns = [
    { title: "ID", dataIndex: "su_id", width: 70 },
    {
      title: "Full Name",
      dataIndex: "full_name",
      width: 200,
      render: (text) => <Text>{text}</Text>,
    },
    { title: "Username", dataIndex: "username", width: 150 },
    { title: "Role", dataIndex: "role", width: 120 },
    {
      title: "Sub Roles",
      dataIndex: "sub_roles",
      width: 200,
      render: (txt) => <span style={{ whiteSpace: "nowrap" }}>{txt}</span>,
    },
    { title: "Dept ID", dataIndex: "dept", width: 80 },
    { title: "Department", dataIndex: "dept_name", width: 200 },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      render: (s) => <Tag color={s === "Active" ? "green" : "red"}>{s}</Tag>,
    },
    {
      title: "Actions",
      width: 80,
      render: (_, row) => (
        <Dropdown overlay={actionMenu(row)} trigger={["click"]}>
          <Button type="text" icon={<MenuOutlined />} />
        </Dropdown>
      ),
    },
  ];

  /* ============================================================
     SEARCH
  ============================================================ */
  const filtered = data.filter((r) =>
    `${r.full_name} ${r.username} ${r.dept_name}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  /* ============================================================
     EXPORT BUTTONS
  ============================================================ */
  const handleCopy = () => {
    const header = ["ID", "Full Name", "Username", "Role", "Dept", "Status"];
    const rows = filtered.map((r) => [
      r.su_id,
      r.full_name,
      r.username,
      r.role,
      r.dept_name,
      r.status,
    ]);

    const csv = [header, ...rows].map((r) => r.join("\t")).join("\n");
    navigator.clipboard.writeText(csv);
    message.success("Copied to clipboard");
  };

  const handleExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filtered);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Staff");
    XLSX.writeFile(wb, "staff_list.xlsx");
  };

  const handlePDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const head = [["ID", "Full Name", "Username", "Role", "Dept", "Status"]];
    const body = filtered.map((r) => [
      r.su_id,
      r.full_name,
      r.username,
      r.role,
      r.dept_name,
      r.status,
    ]);
    doc.autoTable({ head, body });
    doc.save("staff_list.pdf");
  };

  /* ============================================================
     UI LAYOUT
  ============================================================ */
  return (
    <div style={{ padding: 16 }}>
      <Row gutter={16}>
        {/* ======================================================
           LEFT ADD STAFF USER FORM
        ====================================================== */}
        <Col xs={24} md={7} lg={6}>
          <Card title="Manage Staff User" size="small">
            <Form layout="vertical" form={addForm}>
              <Form.Item name="username" label="Username">
                <Input />
              </Form.Item>

              <Form.Item name="full_name" label="Full Name">
                <Input />
              </Form.Item>

              <Form.Item name="password" label="Password">
                <Input.Password />
              </Form.Item>

              <Form.Item name="role" label="Role">
                <Select>
                  <Option value="admin">admin</Option>
                  <Option value="staff">staff</Option>
                </Select>
              </Form.Item>

              <Form.Item name="dept" label="Department ID">
                <Input />
              </Form.Item>

              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="Active">Active</Option>
                  <Option value="Inactive">Inactive</Option>
                </Select>
              </Form.Item>

              <Button type="primary" block onClick={handleAddStaff}>
                Save
              </Button>
            </Form>
          </Card>
        </Col>

        {/* ======================================================
           RIGHT STAFF LIST TABLE
        ====================================================== */}
        <Col xs={24} md={17} lg={18}>
          <Card title="Staff List" size="small">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <Space>
                <Button icon={<CopyOutlined />} onClick={handleCopy}>
                  Copy
                </Button>
                <Button icon={<FileExcelOutlined />} onClick={handleExcel}>
                  Excel
                </Button>
                <Button icon={<FilePdfOutlined />} onClick={handlePDF}>
                  PDF
                </Button>
              </Space>

              <Input
                prefix={<SearchOutlined />}
                placeholder="Search"
                style={{ width: 250 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

           <Table
  columns={columns}
  dataSource={filtered.slice((page - 1) * 10, page * 10)}
  rowKey="su_id"
  loading={loading}
  scroll={{ x: 1300 }}
  pagination={false}
/>


<Pagination
  current={page}
  total={filtered.length}
  pageSize={10}
  showSizeChanger={false}
  onChange={(p) => setPage(p)}
/>


          </Card>
        </Col>
      </Row>

      {/* ======================================================
         EDIT DRAWER
      ====================================================== */}
      <Drawer
        open={editOpen}
        width={350}
        onClose={() => setEditOpen(false)}
        title="Edit Staff User"
      >
        <Form layout="vertical" form={editForm}>
          <Form.Item name="full_name" label="Full Name">
            <Input />
          </Form.Item>

          <Form.Item name="username" label="Username">
            <Input />
          </Form.Item>

          <Form.Item name="password" label="Password">
            <Input.Password placeholder="Leave blank to keep same" />
          </Form.Item>

          <Form.Item name="role" label="Role">
            <Select>
              <Option value="admin">admin</Option>
              <Option value="staff">staff</Option>
            </Select>
          </Form.Item>

          <Form.Item name="sub_roles" label="Sub Roles">
            <Input placeholder='{"1","5","6"}' />
          </Form.Item>

          <Form.Item name="dept" label="Department ID">
            <Input />
          </Form.Item>

          <Form.Item name="status" label="Status">
            <Select>
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Form.Item>

          <Button type="primary" block onClick={handleUpdateStaff}>
            Update
          </Button>
        </Form>
      </Drawer>
    </div>
  );
}
